'use client';

import { useSyncExternalStore } from 'react';

export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const BP_VALUES: readonly [Breakpoint, string][] = [
  ['2xl', '(min-width: 1536px)'],
  ['xl', '(min-width: 1280px)'],
  ['lg', '(min-width: 1024px)'],
  ['md', '(min-width: 768px)'],
  ['sm', '(min-width: 640px)'],
];

/* Audit #1 (2026-05-16): `getSnapshot` previously read matchMedia
   unconditionally on every render. `useSyncExternalStore` guards SSR
   via `getServerSnapshot`, but the contract also requires `getSnapshot`
   to be safe to call at ANY time and in ANY environment. The original
   threw in environments where `window` exists but matchMedia is
   undefined (some test harnesses, JSDOM with matchMedia stubs missing,
   React Native renderers). Tighten the guard so `getSnapshot` returns
   null in any non-browser-like environment. */
function getSnapshot(): Breakpoint | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  for (const [name, query] of BP_VALUES) {
    if (window.matchMedia(query).matches) return name;
  }
  return null;
}

function getServerSnapshot(): Breakpoint | null {
  return null;
}

function subscribe(callback: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }
  const mqls = BP_VALUES.map(([, query]) => window.matchMedia(query));
  for (const mql of mqls) mql.addEventListener('change', callback);
  return () => {
    for (const mql of mqls) mql.removeEventListener('change', callback);
  };
}

/** Returns the current breakpoint name, or `null` during SSR and first
 *  client render. Components must render a layout-stable default when
 *  `bp === null`. Prefer CSS `@media (--bp-md)` or `@container` over
 *  this hook for layout decisions. */
export function useBreakpoint(): Breakpoint | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
